interface LogLevel {
  ERROR: 'error';
  WARN: 'warn';
  INFO: 'info';
  DEBUG: 'debug';
}

const LOG_LEVELS: LogLevel = {
  ERROR: 'error',
  WARN: 'warn',
  INFO: 'info',
  DEBUG: 'debug'
};

interface LogEntry {
  level: keyof LogLevel;
  message: string;
  data?: unknown;
  timestamp: Date;
  context?: string;
}

class Logger {
  private isDevelopment = import.meta.env.DEV;

  private log(level: keyof LogLevel, message: string, data?: unknown, context?: string): void {
    if (!this.isDevelopment && level === 'DEBUG') {
      return;
    }

    const logEntry: LogEntry = {
      level,
      message,
      data,
      timestamp: new Date(),
      context
    };

    if (this.isDevelopment) {
      // In development, use console
      switch (level) {
        case 'ERROR':
          console.error(`[${context || 'APP'}]`, message, data);
          break;
        case 'WARN':
          console.warn(`[${context || 'APP'}]`, message, data);
          break;
        case 'INFO':
          console.info(`[${context || 'APP'}]`, message, data);
          break;
        case 'DEBUG':
          console.debug(`[${context || 'APP'}]`, message, data);
          break;
      }
    } else {
      // In production, send to logging service (implement as needed)
      this.sendToLoggingService(logEntry);
    }
  }

  private sendToLoggingService(logEntry: LogEntry): void {
    // In production, integrate with your logging service
    // Examples: Sentry, LogRocket, DataDog, etc.
    // For now, we'll store critical errors in localStorage as fallback
    if (logEntry.level === 'ERROR') {
      try {
        const errors = JSON.parse(localStorage.getItem('app_errors') || '[]');
        errors.push(logEntry);
        // Keep only last 100 errors
        if (errors.length > 100) {
          errors.splice(0, errors.length - 100);
        }
        localStorage.setItem('app_errors', JSON.stringify(errors));
      } catch {
        // Fail silently if localStorage is not available
      }
    }
  }

  error(message: string, data?: unknown, context?: string): void {
    this.log('ERROR', message, data, context);
  }

  warn(message: string, data?: unknown, context?: string): void {
    this.log('WARN', message, data, context);
  }

  info(message: string, data?: unknown, context?: string): void {
    this.log('INFO', message, data, context);
  }

  debug(message: string, data?: unknown, context?: string): void {
    this.log('DEBUG', message, data, context);
  }
}

export const logger = new Logger();
export { LOG_LEVELS };